import type {
  AdminDelivery,
  AdminMe,
  ActiveWindow,
  ActivityGroup,
  ActivityItem,
  AdminDashboard,
  AdminUser,
  AdminUserDetail,
  Announcement,
  AnnouncementInput,
  ApiEnvelope,
  AuthConfig,
  AuthSession,
  BotLoginPollResult,
  BotLoginRequest,
  DeliveryChannel,
  DeliveryStatus,
  PageMeta,
  Language,
  RetryResult,
  StatsPeriod,
  UserStats,
} from "@/types/api";

import { api, unwrap, unwrapPage } from "./client";

type PageBody<T> = { data: T[]; meta: PageMeta };

/** Bo'sh qiymatlar so'rovga tushmaydi — backend `""` ni filtr deb o'qimasin. */
export function cleanParams<T extends object>(params: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== ""),
  ) as Partial<T>;
}

export const authApi = {
  config: () => unwrap(api.get<ApiEnvelope<AuthConfig>>("/auth/config")),
  botLogin: () => unwrap(api.post<ApiEnvelope<BotLoginRequest>>("/auth/telegram/bot-login")),
  pollBotLogin: (token: string) =>
    unwrap(api.post<ApiEnvelope<BotLoginPollResult>>("/auth/telegram/bot-login/poll", { token })),
  devLogin: (telegramUserId: string) =>
    unwrap(api.post<ApiEnvelope<AuthSession>>("/auth/dev-login", { telegramUserId })),
  logout: () => api.post("/auth/logout"),
};

export interface UserFilters {
  page: number;
  limit?: number;
  search?: string;
  blocked?: boolean;
  activity?: "active" | "inactive";
}

export interface DashboardFilters {
  from?: string;
  to?: string;
  language?: Language;
  channel?: DeliveryChannel;
  activeDays?: ActiveWindow;
}

export interface UserStatsFilters {
  period: StatsPeriod;
  date?: string;
}

export interface ActivityFilters {
  page: number;
  limit?: number;
  group?: ActivityGroup;
}

export interface AnnouncementFilters {
  page: number;
  limit?: number;
}

export interface DeliveryFilters {
  page: number;
  limit?: number;
  status?: DeliveryStatus;
  channel?: DeliveryChannel;
}

export const adminApi = {
  me: () => unwrap(api.get<ApiEnvelope<AdminMe>>("/admin/me")),
  dashboard: (filters: DashboardFilters) =>
    unwrap(
      api.get<ApiEnvelope<AdminDashboard>>("/admin/dashboard", { params: cleanParams(filters) }),
    ),
  userStats: (id: string, filters: UserStatsFilters) =>
    unwrap(
      api.get<ApiEnvelope<UserStats>>(`/admin/users/${id}/stats`, { params: cleanParams(filters) }),
    ),
  userActivity: (id: string, filters: ActivityFilters) =>
    unwrapPage(
      api.get<PageBody<ActivityItem>>(`/admin/users/${id}/activity`, {
        params: cleanParams(filters),
      }),
    ),
  users: (filters: UserFilters) =>
    unwrapPage(api.get<PageBody<AdminUser>>("/admin/users", { params: cleanParams(filters) })),
  user: (id: string) => unwrap(api.get<ApiEnvelope<AdminUserDetail>>(`/admin/users/${id}`)),
  block: (id: string) => unwrap(api.post<ApiEnvelope<AdminUserDetail>>(`/admin/users/${id}/block`)),
  unblock: (id: string) =>
    unwrap(api.post<ApiEnvelope<AdminUserDetail>>(`/admin/users/${id}/unblock`)),
  deliveries: (filters: DeliveryFilters) =>
    unwrapPage(
      api.get<PageBody<AdminDelivery>>("/admin/deliveries", { params: cleanParams(filters) }),
    ),
  retryDelivery: (id: string) =>
    unwrap(api.post<ApiEnvelope<RetryResult>>(`/admin/deliveries/${id}/retry`)),
  announcements: (filters: AnnouncementFilters) =>
    unwrapPage(
      api.get<PageBody<Announcement>>("/admin/announcements", { params: cleanParams(filters) }),
    ),
  announce: (input: AnnouncementInput) =>
    unwrap(api.post<ApiEnvelope<Announcement>>("/admin/announcements", cleanParams(input))),
};
